<template>
  <div
    class="d-post-book"
    :disabled="isDisabled"
  >
    <DuiBookCover :book-cover-url="bookCoverUrl" />

    <div class="fixed-size">
      <div
        v-if="!!book"
      >
        <div class="grid-flex grid-no-response">
          <div class="grid-item contain">
            <h3 class="space-bottom-none ellipsis">
              {{ book.title }}
            </h3>
            <p
              v-if="book.author"
              class="font-alt ellipsis"
            >
              {{ book.author }}
            </p>
          </div>
          <div class="grid-item-shrink">
            <button
              class="button-grey button-icon button-sm button-close"
              title="Cancel"
              @click="clearBookInfo()"
            />
          </div>
        </div>

        <p class="book-url-container font-alt">
          <a
            class="ellipsis"
            :href="book.url"
            :title="book.title"
          >
            {{ book.url }}
          </a>
        </p>
      </div>

      <div
        v-else
      >
        <div class="grid-flex grid-no-response">
          <div class="grid-item">
            Find a book from
            <a
              :href="appUrls.discovery"
              target="_blank"
              title="Discovery"
            >Discovery</a>,
            <a
              :href="appUrls.amazon"
              rel="nofollow"
              target="_blank"
              title="Amazon"
            >Amazon</a>,
            <a
              :href="appUrls.googlePlay"
              rel="nofollow"
              target="_blank"
              title="Google Play"
            >Google Play</a> or
            <a
              :href="appUrls.appleBooks"
              rel="nofollow"
              target="_blank"
              title="Apple Books"
            >Apple Books</a>.
          </div>
          <div
            v-if="!postInfo.isEditing"
            class="grid-item-shrink"
          >
            <button
              class="button-grey button-icon button-sm button-close"
              title="Cancel"
              @click="clearPostType()"
            />
          </div>
        </div>

        <div
          ref="bookUrl"
          class="book-url-input labeled-input size-sm space-top-1em space-bottom-1em"
        >
          <label>URL</label>
          <input
            v-model="bookUrl"
            class="input-full"
            :disabled="isProcessing"
            placeholder="Enter a URL"
            type="text"
            @input="debounceSearch"
          >
          <DuiLoadingIndicator
            v-if="isSearchingBook"
            size="sm"
          />
        </div>
      </div>

      <div class="grid-flex">
        <div
          v-if="withRating"
          class="grid-item"
        >
          <DuiRating
            v-model="rating"
            class="space-right-1em"
            :disabled="isProcessing"
            :editable="true"
            @mouse-over-performed="ratingMouseOverPerformed"
            @mouse-out-performed="ratingMouseOutPerformed"
          />

          <span
            ref="ratingMessage"
            class="rating-message valign-middle space-right-1em"
          >
            <span v-if="!!ratingMessage">{{ ratingMessage }}</span>
            <span v-else>Add a rating</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { required, url } from 'vuelidate/lib/validators';

import bookInformationService from '@d-services/book-information-service/book-information-service';
import { debounce } from '@dui/helpers/util-funcs';
import { DEBOUNCE_TIME } from '@d-shared/config';
import { getErrorMessage } from '@d-shared/utils/errors';
import { POST_TYPES } from '@d-shared/config';
import postTypeMixin from '../post-type-mixin';
import URLS from '@d-shared/urls/urls';
import verdictsService from '@d-services/verdicts-service/verdicts-service';

export default {
  mixins: [
    postTypeMixin,
  ],
  props: {
    isDisabled: {
      type: Boolean,
      default: false,
    },
    postInfo: {
      type: Object,
      required: true,
    },
    withRating: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    appUrls: {
      amazon: URLS.external.amazon.books,
      appleBooks: URLS.external.apple.books,
      discovery: URLS.app.discover,
      googlePlay: URLS.external.google.playBooks,
    },
    book: null,
    bookUrl: null,
    isProcessing: false,
    isSearchingBook: false,
    rating: null,
    ratingMessages: null,
    ratingHoverValue: null,
  }),
  computed: {
    bookCoverUrl() {
      if (!this.book) {
        return null;
      }

      if (this.book.cover) {
        return this.book.cover.small.url
      }

      if (this.book.covers) {
        return this.book.covers.small;
      }

      return null;
    },
    ratingMessage() {
      if (!this.ratingMessages || !this.rating && !this.ratingHoverValue) {
        return null;
      }

      const ratingValue = this.ratingHoverValue ? this.ratingHoverValue : this.rating;
      const verdict = this.ratingMessages.find(m => m.rating === ratingValue);
      if (!verdict) {
        return null;
      }

      return verdict.name;
    },
  },
  validations: {
    bookUrl: { required, url },
  },
  watch: {
    rating: function() {
      this.$set(this.postInfo, 'recommendationRating', this.rating);
    },
  },
  beforeDestroy() {
    this.clearBookInfo();
  },
  mounted() {
    this.init();
  },
  methods: {
    blinkRating() {
      this.$refs.ratingMessage.classList.remove('blink');
      setTimeout(() => {
        this.$refs.ratingMessage.classList.add('blink');
      }, 100);
    },

    blinkUrl() {
      this.$refs.bookUrl.classList.remove('blink');
      setTimeout(() => {
        this.$refs.bookUrl.classList.add('blink');
      }, 100);
    },

    clearBookInfo() {
      this.$set(this, 'book', null);
      this.$set(this, 'bookUrl', null);
      this.$set(this, 'rating', null);
      this.$set(this.postInfo, 'bookFromUrl', null);
      this.$set(this.postInfo, 'bookUrl', null);
      this.$set(this.postInfo, 'bookUrlInvalid', false);
      this.$set(this.postInfo, 'recommendationRating', null);
    },

    debounceSearch: debounce(function() {
      this.searchExternalBook();
    }, DEBOUNCE_TIME.normal),

    init() {
      if (this.withRating) {
        this.loadVerdicts();
      }

      this.setEditingContent();
    },

    async loadVerdicts() {
      this.$set(this, 'isProcessing', true);

      try {
        const verdicts = await verdictsService.getVerdictOptions();
        this.$set(this, 'ratingMessages', verdicts);
      } catch(error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isProcessing', false);
    },

    ratingMouseOutPerformed() {
      this.ratingHoverValue = null;
    },

    ratingMouseOverPerformed(value) {
      this.ratingHoverValue = value;
    },

    async searchExternalBook() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$refs.bookUrl.classList.add('invalid');
        this.$set(this.postInfo, 'bookUrl', null);
        this.$set(this.postInfo, 'bookUrlInvalid', true);
        return;
      }

      this.$refs.bookUrl.classList.remove('invalid');
      this.$set(this.postInfo, 'bookUrlInvalid', false);

      this.$set(this, 'isSearchingBook', true);

      try {
        const bookInfo = await bookInformationService.getBookInfoFromUrl(this.bookUrl.trim());
        if (!bookInfo || !bookInfo.url || !bookInfo.title) {
          this.$set(this, 'book', null);
          this.$set(this.postInfo, 'bookFromUrl', null);
          this.$set(this.postInfo, 'bookUrl', this.bookUrl.trim());
          Vue.duiNotificationManager.message('Book not found.');
          this.$set(this, 'isSearchingBook', false);
          return;
        }

        this.$set(this, 'book', bookInfo);
        this.$set(this, 'bookUrl', null);
        this.$set(this.postInfo, 'bookUrl', null);
        this.$set(this.postInfo, 'bookFromUrl', bookInfo);
      } catch(error) {
        if (error.response && error.response.status === 404) {
          this.$set(this, 'book', null);
          this.$set(this.postInfo, 'bookFromUrl', null);
          this.$set(this.postInfo, 'bookUrl', this.bookUrl.trim());
          Vue.duiNotificationManager.message('Book not found.');
          this.$set(this, 'isSearchingBook', false);
          return;
        }

        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isSearchingBook', false);
    },

    setEditingContent() {
      if (this.postInfo.bookFromUrl) {
        this.$set(this, 'book', this.postInfo.bookFromUrl);
        this.$set(this, 'bookUrl', this.postInfo.bookFromUrl.url);
        this.$set(this, 'rating', this.postInfo.recommendationRating);
        return;
      }

      if (this.postInfo.bookUrl) {
        this.$set(this, 'bookUrl', this.postInfo.bookUrl);
        this.$set(this, 'rating', this.postInfo.recommendationRating);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

.d-post-book {
  display: grid;
  grid-column-gap: $_1em;
  grid-template-columns: auto 1fr;

  @include screenMoreThan(sm) {
    grid-column-gap: $_2em;
  }

  &[disabled] {
    opacity: $disabled-opacity;
    pointer-events: none;
  }

  .dui-book-cover {
    height: $book-cover-height-xxs;
    width: $book-cover-width-xxs;

    @include screenMoreThan(sm) {
      height: $book-cover-height-xs;
      width: $book-cover-width-xs;
    }
  }

  .cant-find-action {
    color: getColor($grey, dark);
    font-weight: normal;
  }

  .fixed-size {
    min-width: 0;
  }

  .contain {
    overflow: hidden;
  }

  .book-url-input.blink,
  .book-url-input.blink input,
  .rating-message.blink {
    animation-duration: $transition-time-blink;
    animation-fill-mode: forwards;
  }

  .book-url-input {
    &.invalid {
      color: $color-invalid;

      input {
        border-color: $color-invalid;
      }
    }

    &.blink {
      animation-name: blink-color-red;

      input {
        animation-name: blink-border-red;
      }
    }

    input {
      padding-right: $_6em;
    }

    .dui-loading-indicator {
      position: absolute;
      right: $_2em;
      top: calc(50% - #{ $border-width-base });
      transform: translateY(-50%);
      z-index: 1;
    }
  }

  .book-url-container {
    height: $_3em;
    position: relative;

    a {
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }
  }

  .rating-message {
    color: getColor($grey, dark);

    &.blink {
      animation-name: blink-color-red;
    }
  }
}
</style>
